import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, Info, XCircle } from "lucide-react";
import type { CilDecision, CilExplanation, CilExplanationCheck, CilTimelineEntry, CilTimelineRole } from "@hp/types";
import { Badge, Button } from "@hp/ui";
import { fetchCilExplanation } from "../../api/cil";
import { ErrorState, LoadingState } from "../Feedback";
import { formatDate } from "../../lib/format";
import { DocumentLinks, LinkedText } from "./DocumentLinks";

/** Who each timeline step belongs to, and how it is drawn. */
const ROLE: Record<CilTimelineRole, { tag: string; dot: string; tone: "success" | "danger" | "warning" | "neutral" | "info" | "brand" }> = {
  hp_position: { tag: "HP · this event", dot: "bg-indigo-600 ring-indigo-200", tone: "brand" },
  hp_history: { tag: "HP · earlier order", dot: "bg-indigo-300 ring-indigo-100", tone: "neutral" },
  trigger: { tag: "Raised this event", dot: "bg-amber-500 ring-amber-200", tone: "warning" },
  precedent: { tag: "Ruling HP relies on", dot: "bg-sky-500 ring-sky-200", tone: "info" },
  peer_history: { tag: "Other generator", dot: "bg-zinc-400 ring-zinc-200", tone: "neutral" },
  stay: { tag: "Stay", dot: "bg-amber-500 ring-amber-200", tone: "warning" },
  appeal_pending: { tag: "Appeal pending", dot: "bg-sky-300 ring-sky-100", tone: "info" },
  set_aside: { tag: "Set aside", dot: "bg-rose-500 ring-rose-200", tone: "danger" },
  upheld: { tag: "Upheld", dot: "bg-emerald-500 ring-emerald-200", tone: "success" },
  related_proceeding: { tag: "Related SC proceeding", dot: "bg-zinc-300 ring-zinc-100", tone: "neutral" },
};

const VERDICT: Record<CilExplanationCheck["verdict"], { icon: React.ComponentType<{ className?: string }>; className: string }> = {
  pass: { icon: CheckCircle2, className: "text-emerald-600" },
  warn: { icon: AlertTriangle, className: "text-amber-600" },
  fail: { icon: XCircle, className: "text-rose-600" },
  info: { icon: Info, className: "text-sky-600" },
};

const TONE_BORDER: Record<CilExplanation["status_tone"], string> = {
  success: "border-emerald-300 bg-emerald-50/60",
  danger: "border-rose-300 bg-rose-50/60",
  warning: "border-amber-300 bg-amber-50/60",
  neutral: "border-zinc-300 bg-zinc-50",
  info: "border-sky-300 bg-sky-50/60",
};

/** The "What happens next" (If / Then) table is hidden for now; flip to show it. */
const SHOW_NEXT_STEPS = false;
/** The "In the card's number?" line is hidden for now; flip to show it. */
const SHOW_COUNTING = false;

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000);
}

/** "105 days before HP's order", "3 years after HP's order". */
function relativeLabel(entry: CilTimelineEntry, hpDate: string | null): string | null {
  if (entry.role === "hp_position") return "HP's order";
  if (!entry.date || !hpDate || !entry.relative_to_hp) return null;
  if (entry.relative_to_hp === "same_day") return "same day as HP's order";
  const days = Math.abs(daysBetween(entry.date, hpDate));
  const span = days >= 730 ? `${Math.round(days / 365)} years` : days >= 60 ? `${Math.round(days / 30)} months` : `${days} day${days === 1 ? "" : "s"}`;
  return `${span} ${entry.relative_to_hp} HP's order`;
}

function TimelineItem({
  entry,
  hpDate,
  last,
  onViewSource,
}: {
  entry: CilTimelineEntry;
  hpDate: string | null;
  last: boolean;
  onViewSource: (decision: CilDecision) => void;
}) {
  const [showQuote, setShowQuote] = React.useState(entry.role === "hp_position" || entry.role === "trigger");
  const role = ROLE[entry.role];
  const relative = relativeLabel(entry, hpDate);
  const highlight = entry.role === "hp_position" || entry.role === "trigger";
  return (
    <li className="relative flex gap-3 pb-4">
      <div className="w-24 shrink-0 pt-0.5 text-right text-xs font-medium text-zinc-600">{entry.date ? formatDate(entry.date) : "Undated"}</div>
      <div className="relative flex w-4 shrink-0 justify-center">
        {!last ? <span className="absolute top-3 h-full w-px bg-zinc-200" aria-hidden /> : null}
        <span className={`relative mt-1 h-3 w-3 rounded-full ring-4 ${role.dot}`} aria-hidden />
      </div>
      <div className={`min-w-0 flex-1 rounded-md ${highlight ? "border border-zinc-200 bg-white p-2.5 shadow-sm" : "pt-0"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={role.tone}>{role.tag}</Badge>
          {relative ? <span className="text-xs text-zinc-500">{relative}</span> : null}
        </div>
        <p className={`mt-1 text-sm ${highlight ? "font-medium text-zinc-900" : "text-zinc-800"}`}>
          <LinkedText text={entry.title} />
        </p>
        {entry.detail ? (
          <p className="text-xs text-zinc-600">
            <LinkedText text={entry.detail} />
          </p>
        ) : null}
        {entry.caution ? (
          <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden /> {entry.caution}
          </p>
        ) : null}
        {entry.decision ? (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setShowQuote((v) => !v)}>
              {showQuote ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
              {showQuote ? "Hide wording" : "Show wording"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onViewSource(entry.decision!)} disabled={!entry.decision.has_source_document}>
              <FileText className="h-3.5 w-3.5" aria-hidden /> {entry.decision.has_source_document ? `View source (p${entry.decision.page})` : "Source not stored"}
            </Button>
          </div>
        ) : null}
        {entry.decision && showQuote ? (
          <blockquote className="mt-1 border-l-2 border-zinc-300 pl-3 text-sm italic text-zinc-700">
            “{entry.decision.quote}” <span className="not-italic text-zinc-500">p{entry.decision.page}</span>
          </blockquote>
        ) : null}
      </div>
    </li>
  );
}

/** "The appeal is dismissed" -> "If the appeal is dismissed"; names
 * ("Supreme Court sets aside ...", "APTEL Appeal ...") keep their capitals. */
function scenarioCondition(condition: string): string {
  if (/^until\b/i.test(condition)) return condition;
  const lower = /^(The|A|An|No|Nobody|Another|Any|It|Its)\b/.test(condition);
  return `If ${lower ? condition.charAt(0).toLowerCase() + condition.slice(1) : condition}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</h4>
      {children}
    </section>
  );
}

export function ChangeEventJustificationView({
  explanation,
  onViewSource,
}: {
  explanation: CilExplanation;
  onViewSource: (decision: CilDecision) => void;
}) {
  const hpDate = explanation.timeline.find((e) => e.role === "hp_position")?.date ?? null;
  return (
    <DocumentLinks
      links={explanation.documents ?? []}
      parties={explanation.timeline.flatMap((e) => (e.decision?.party_name ? [e.decision.party_name] : []))}
    >
    <div className="flex flex-col gap-4">
      <div className={`rounded-lg border p-3 ${TONE_BORDER[explanation.status_tone]}`}>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">Where HP stands</span>
          <Badge tone={explanation.status_tone}>{explanation.status_label}</Badge>
        </div>
        <p className="text-sm font-medium text-zinc-900">
          <LinkedText text={explanation.headline} />
        </p>
        <p className="mt-2 text-sm text-zinc-700">
          <LinkedText text={explanation.what_changed} />
        </p>
      </div>

      <Section title="Timeline: every ruling on this cost, in date order">
        <ol className="flex flex-col">
          {explanation.timeline.map((entry, i) => (
            <TimelineItem
              key={`${entry.role}-${entry.decision?.id ?? i}`}
              entry={entry}
              hpDate={hpDate}
              last={i === explanation.timeline.length - 1}
              onViewSource={onViewSource}
            />
          ))}
        </ol>
      </Section>

      <Section title="Why the system flagged this">
        <ul className="flex flex-col gap-2">
          {explanation.checks.map((check) => {
            const verdict = VERDICT[check.verdict];
            const Icon = verdict.icon;
            return (
              <li key={check.label} className="flex items-start gap-2 text-sm">
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${verdict.className}`} aria-hidden />
                <span>
                  <span className="font-medium text-zinc-900">{check.label}. </span>
                  <span className="text-zinc-700">
                    <LinkedText text={check.explanation} />
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      {SHOW_NEXT_STEPS ? (
      <Section title="What happens next">
        <div className="overflow-hidden rounded-md border border-zinc-200">
          <table className="w-full text-left text-sm">
            <tbody>
              {explanation.scenarios.map((s) => (
                <tr key={s.condition} className="border-b border-zinc-100 last:border-0 align-top">
                  <th scope="row" className="w-2/5 bg-zinc-50 px-3 py-2 font-medium text-zinc-800">
                    <LinkedText text={scenarioCondition(s.condition)} />
                  </th>
                  <td className="px-3 py-2 text-zinc-700">
                    <LinkedText text={s.outcome} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      ) : null}

      {SHOW_COUNTING ? (
        <p className="text-xs text-zinc-600">
          <span className="font-medium">In the card's number? </span>
          <LinkedText text={explanation.counting} />
        </p>
      ) : null}
    </div>
    </DocumentLinks>
  );
}

/** Loads and shows the full justification for one changed event. */
export function ChangeEventJustification({ eventId, onViewSource }: { eventId: string; onViewSource: (decision: CilDecision) => void }) {
  const query = useQuery({ queryKey: ["cil", "explanation", eventId], queryFn: () => fetchCilExplanation(eventId) });
  if (query.isPending) return <LoadingState label="Building the justification..." />;
  if (query.isError) return <ErrorState error={query.error} label="Could not load the justification" />;
  return <ChangeEventJustificationView explanation={query.data} onViewSource={onViewSource} />;
}
